'use client'

// SUMMARY: The in-chat QUIZ card - a mixed multiple-choice + free-text form the document-editor assistant
// emits when it needs a few facts before editing. Renders each question (choice buttons or a text input) and,
// on submit, posts a readable "- question: answer" message plus a hidden result block back through
// DocumentFormContext (the DOCUMENT chat panel's send, never the main chat). Answered cards render inert.

import { useState } from 'react'
import { useLocale } from 'next-intl'
import { Sparkles, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { encodeQuizResult, type QuizPrompt, type QuizChoiceQuestion } from '@/lib/ai/quiz-form'
import { useDocumentFormActions } from './document-form-context'

const INPUT_CLASS =
	'w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring/50'

export function QuizCard({ prompt }: { prompt: QuizPrompt }) {
	const de = useLocale() === 'de'
	const actions = useDocumentFormActions()
	// One map: choice answers are the selected option strings, text answers a single-element array.
	const [values, setValues] = useState<Record<string, string[]>>({})
	const [submitted, setSubmitted] = useState(false)

	const toggle = (q: QuizChoiceQuestion, option: string) =>
		setValues((prev) => {
			const cur = prev[q.key] ?? []
			const next = q.multi ? (cur.includes(option) ? cur.filter((o) => o !== option) : [...cur, option]) : [option]
			return { ...prev, [q.key]: next }
		})
	const setText = (key: string, value: string) => setValues((prev) => ({ ...prev, [key]: [value] }))

	// Locked = already submitted, or this card isn't the live one (a busy turn / an earlier / reloaded card).
	const locked = submitted || !!actions?.disabled
	// Every choice question needs a selection; text answers are optional.
	const choiceIncomplete = prompt.questions.some((q) => q.kind === 'choice' && !values[q.key]?.length)
	const canSubmit = !locked && !choiceIncomplete

	const submit = () => {
		if (!actions || !canSubmit) return
		const answers = prompt.questions.map((q) => {
			const val = values[q.key] ?? []
			const answer =
				q.kind === 'choice'
					? q.options.filter((o) => val.includes(o)).join(', ')
					: val[0]?.trim() || (de ? '(keine Angabe)' : '(not provided)')
			return { key: q.key, question: q.question, kind: q.kind, answer }
		})
		const humanText = `${de ? 'Meine Antworten:' : 'My answers:'}\n${answers
			.map((a) => `- ${a.question}: ${a.answer}`)
			.join('\n')}`
		setSubmitted(true)
		actions.onSubmit(encodeQuizResult({ humanText, answers, state: prompt.state }))
	}

	return (
		<div className="my-3 overflow-hidden rounded-xl border border-border">
			<div className="flex items-center gap-2 border-b border-border bg-foreground/[0.03] px-3.5 py-2.5">
				<Sparkles className="h-4 w-4 shrink-0 text-primary" />
				<span className="text-sm font-semibold">{prompt.title || (de ? 'Kurze Rückfragen' : 'A few questions')}</span>
			</div>
			<div className="space-y-3.5 p-3.5">
				{prompt.questions.map((q) => (
					<div key={q.key} className="space-y-1.5">
						<p className="text-sm font-medium">{q.question}</p>
						{q.kind === 'choice' ? (
							<div className="space-y-1.5">
								{q.options.map((option) => {
									const active = (values[q.key] ?? []).includes(option)
									return (
										<button
											key={option}
											type="button"
											disabled={locked}
											onClick={() => toggle(q, option)}
											className={cn(
												'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
												active ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:bg-accent',
												locked && 'cursor-default opacity-70'
											)}
										>
											<span
												className={cn(
													'flex h-4 w-4 shrink-0 items-center justify-center border',
													q.multi ? 'rounded-[4px]' : 'rounded-full',
													active ? 'border-blue-500 bg-blue-500 text-white' : 'border-input'
												)}
											>
												{active && <Check className="h-3 w-3" />}
											</span>
											<span className="min-w-0 break-words">{option}</span>
										</button>
									)
								})}
							</div>
						) : (
							<input
								type="text"
								disabled={locked}
								value={values[q.key]?.[0] ?? ''}
								placeholder={q.placeholder}
								onChange={(e) => setText(q.key, e.target.value)}
								className={cn(INPUT_CLASS, locked && 'opacity-70')}
							/>
						)}
					</div>
				))}
				<Button onClick={submit} disabled={!canSubmit} className="mt-1 w-full">
					{submitted ? (de ? 'Übermittelt' : 'Submitted') : de ? 'Absenden' : 'Submit'}
				</Button>
			</div>
		</div>
	)
}
